import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, X, FileText } from "lucide-react";

// Reusable uploader. mode="images" (multiple, returns comma string), "image" (single URL), "file" (pdf/single URL)
export default function FileUploader({ mode = "image", value, onChange, accept, label }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const multiple = mode === "images";
  const isImage = mode !== "file";

  const urls = multiple ? (value ? value.split(",").map((s) => s.trim()).filter(Boolean) : []) : (value ? [value] : []);

  const upload = async (files) => {
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${API}/files/${data.path}`);
      }
      if (multiple) onChange([...urls, ...uploaded].join(", "));
      else onChange(uploaded[0]);
      toast.success("Uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (i) => {
    if (multiple) onChange(urls.filter((_, idx) => idx !== i).join(", "));
    else onChange("");
  };

  return (
    <div>
      {label && <p className="text-xs font-semibold text-navy mb-1">{label}</p>}
      <div className="flex flex-wrap gap-3">
        {urls.map((u, i) => (
          <div key={i} className="relative group">
            {isImage ? (
              <img src={u} alt="" className="w-20 h-20 rounded-lg object-cover border border-gray-200" />
            ) : (
              <div className="w-20 h-20 rounded-lg border border-gray-200 flex flex-col items-center justify-center bg-muted"><FileText className="w-6 h-6 text-gold" /><span className="text-[9px] text-muted-foreground mt-1">PDF</span></div>
            )}
            <button type="button" onClick={() => removeAt(i)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-upload"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="upload-trigger"
          className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-muted-foreground hover:border-gold hover:text-gold transition-colors">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-5 h-5" /><span className="text-[10px] mt-1">Upload</span></>}
        </button>
      </div>
      <input ref={inputRef} type="file" hidden multiple={multiple} accept={accept || (isImage ? "image/*" : "application/pdf")} onChange={(e) => e.target.files.length && upload(Array.from(e.target.files))} />
    </div>
  );
}
